import { DropMode } from 'base/ui/dnd';
import { computed, makeObservable } from 'mobx';
import { Assistant } from '../ai/assistant';
import {
  getCompanyDescriptionError,
  getPageContextError,
  getPageDescriptionError,
  getPageNameError,
} from '../ai/validation';
import { EMPTY_PROJECT } from '../default_project';
import {
  EditorMode,
  editorModeStore,
  isWireframeOrDesign,
} from '../editor_mode';
import { DEFAULT_GENERATE_PAGE_COUNT } from '../edit_panel/space';
import {
  aPage,
  Page,
  pageDomain,
  PageState,
  Project,
  SectionRef,
  SectionRefState,
  Space,
  spaceDomain,
} from '../model';
import { removeAllThreadsFromPage } from '../model_comment_helpers';
import {
  elementTreeDfs,
  findParentSpace,
  parentsMap,
  unrefPresentSections,
} from '../model_helpers';
import { SelectionStore } from '../selection/store';
import { ProjectSession } from '../session/session';
import { SidebarNavigation } from '../sidebar/routes';
import { PagePosition, PageSource } from '../sitemap/page_dnd';

export class PageEditingActions {
  constructor(
    private readonly selection: SelectionStore,
    private readonly session: ProjectSession,
    private readonly assistant: Assistant,
    private readonly sidebarNavigation: SidebarNavigation,
    private readonly space: Space | 'home',
    private readonly self: Page,
  ) {
    makeObservable(this);
  }

  @computed
  get parent() {
    return parentsMap(this.selection.project).get(this.self);
  }

  readonly setName = (name: string) => {
    this.self.name = name;
  };

  readonly setDescription = (description: string) => {
    this.self.description = description;
  };

  @computed
  get deleteSelfAndSubPages() {
    const { space, parent, self } = this;
    const { project } = this.selection;
    if (parent == null) {
      // Special case - project.homePage is reset not deleted
      return () => clearHomepage(project);
    }

    return () => {
      const parentSelection = this.selection.factory.treeParentOfPage(self);
      const index = parent.subPages.indexOf(self);
      parent.subPages.delete(self);
      // Prefer to select the next (right) page than the previous (left)
      const neighbour = parent.subPages[index] || parent.subPages[index - 1];
      if (neighbour) {
        this.selection.set({ space, page: neighbour });
      } else if (
        spaceDomain.isMutable(parent) &&
        parent.subPages.length === 0
      ) {
        const index = project.spaces.indexOf(parent);
        // delete the empty space
        project.spaces.delete(parent);
        const previousSpace = project.spaces[index - 1];
        this.selection.set(
          previousSpace
            ? { space: previousSpace, page: previousSpace.subPages[0] }
            : {
                space: 'home',
                page: project.homePage,
              },
        );
      } else {
        this.selection.set(parentSelection);
      }
    };
  }

  @computed
  get deleteSelfOnly() {
    const { parent, self, deleteSelfAndSubPages: deleteTree } = this;
    if (!parent || !deleteTree) return;
    return () => {
      parent.subPages.splice(
        parent.subPages.indexOf(self) + 1,
        0,
        ...self.subPages.state,
      );
      deleteTree();
    };
  }

  readonly duplicate = () => {
    const { space, parent, self } = this;
    const arr = parent?.subPages || self.subPages;
    const idx = arr.indexOf(self) + 1;

    // Comment threads are not copied over on duplication,
    // so remove them before splicing in the new page.
    arr.splice(idx, 0, {
      ...removeAllThreadsFromPage(self.state),
      subPages: [],
    });
    this.selection.set({ space, page: arr[idx] });
  };

  /** Adds a page after the current page, or a subpage if not possible */
  readonly addPageNatural = (state?: PageState) => {
    const { self, parent, selection } = this;
    if (parent) {
      const index = parent.subPages.indexOf(self);
      insertPage(parent, index + 1, selection, state);
    } else {
      insertPage(self, self.subPages.length, selection, state);
    }
  };

  /** Adds a page so it'd be before the current page (in a DFS traversal) */
  @computed
  get addPageBefore() {
    const { parent, self } = this;
    if (!parent) return;
    return () => {
      const index = parent.subPages.indexOf(self);
      insertPage(parent, index, this.selection);
    };
  }

  /** Adds a page so it'd be after the current page (in a DFS traversal) */
  readonly addPageAfter = () => {
    const { parent, self } = this;
    if (parent) {
      const index = parent.subPages.indexOf(self);
      insertPage(parent, index + 1, this.selection);
    } else {
      insertPage(self, 0, this.selection);
    }
  };

  readonly insertSection = (
    section: SectionRefState,
    afterSection?: SectionRef,
  ) => {
    const { self, space } = this;
    const index = afterSection
      ? self.sections.indexOf(afterSection) + 1
      : self.sections.length;
    self.sections.splice(index, 0, section);
    this.selection.set({
      space,
      page: self,
      section: self.sections[index],
    });
  };

  @computed
  get setAsHomePage() {
    const { space, parent, self } = this;
    if (space === 'home' && parent == null) return;

    // Simple case - re-ordering top level pages (in a Space)
    if (space !== 'home' && parent === space) {
      const indexInSpace = space.subPages.indexOf(this.self);
      if (indexInSpace === 0) return;
      return () => {
        const oldHome = space.subPages[0];
        if (oldHome) {
          oldHome.name = maybeRenameToOldName(oldHome.name);
        }
        space.subPages.splice(indexInSpace, 1);
        space.subPages.splice(0, 0, self.state);
        this.selection.set({ space, page: space.subPages[0] });
      };
    }

    // Complex case - move self into home page, and move home page down
    const home =
      space === 'home' ? this.selection.project.homePage : space.subPages[0];
    return () => {
      const demote: PageState = {
        ...home.state,
        name: maybeRenameToOldName(home.name),
        subPages: [],
      };
      home.subPages.splice(0, 0, demote, ...self.subPages.state);
      parent?.subPages.delete(self);
      home.name = 'Home';
      home.sections.clear();
      home.sections.push(...self.sections.state);
      this.selection.set({ space, page: home });
    };
  }

  readonly makeWireframeVisible = () => {
    const { self, selection } = this;
    editorModeStore.set(EditorMode.Wireframe);
    selection.set(selection.factory.forPage(self), {
      applyMinimumSize: true,
    });
  };

  /** Starts generating a page, or asks the user for more information if needed. */
  readonly regenerateOrOpenForm = () => {
    const { self, parent, space } = this;
    const { project } = this.selection;

    if (
      isWireframeOrDesign(editorModeStore.get()) &&
      !this.session.canViewWireframe(self)
    ) {
      this.selection.set({ space, page: self });
      this.sidebarNavigation.goto({ path: 'edit' });
      return;
    }

    const chooseComponents = isWireframeOrDesign(editorModeStore.get());
    const isHomeOfSpace =
      parent == null || (parent === space && space.subPages.length === 1);
    if (isHomeOfSpace && self.subPages.length === 0) {
      // If you click generate on an empty home page, we will generate subpages too
      this.selection.set({ space });
      const error = getCompanyDescriptionError(
        (space !== 'home' && space.description) || project.brief || '',
      );
      if (error == null) {
        this.assistant.generateSpace(space, {
          pageCount: DEFAULT_GENERATE_PAGE_COUNT,
          chooseComponents,
          generateCopy: chooseComponents,
        });
      } else {
        this.sidebarNavigation.goto({ path: 'edit' });
      }
    } else {
      // Otherwise, generate just this page
      const sel = { space, page: self };
      this.selection.set(sel);
      if (
        getPageContextError(self.state, space, this.selection.project.state) ==
          null &&
        getPageNameError(self.name) == null &&
        getPageDescriptionError(self.description) == null
      ) {
        this.assistant.generatePages([self], {
          chooseComponents,
          generateCopy: chooseComponents,
        });
      } else {
        this.sidebarNavigation.goto({ path: 'edit' });
      }
    }
  };

  private get canGenerateCopyOrShuffle() {
    const { self, space } = this;
    const sel = { space, page: self };
    return (
      getPageContextError(
        self.state,
        sel.space,
        this.selection.project.state,
      ) == null &&
      getPageNameError(self.name) == null &&
      getPageDescriptionError(self.description) == null
    );
  }

  readonly generateCopyOrOpenForm = () => {
    const { self, space } = this;
    this.selection.set({ space, page: self });
    if (this.canGenerateCopyOrShuffle) {
      this.session.analytics.track({
        event: 'project:copy_generate',
        copy_generate_scope: 'page',
        has_copy: this.resetCopy != null,
      });
      this.assistant.generateCopy(
        new Map(self.sections.map((s) => [s, undefined])),
      );
    } else {
      this.sidebarNavigation.goto({ path: 'edit' });
    }
  };

  readonly shuffleComponentsOrOpenForm = () => {
    const { self, space } = this;
    this.selection.set({ space, page: self });
    if (this.canGenerateCopyOrShuffle) {
      this.session.analytics.track({
        event: 'project:components_shuffle',
        components_shuffle_scope: 'page',
      });
      this.assistant.chooseComponents(self, {
        replaceExistingComponents: true,
        generateCopy: true,
      });
    } else {
      this.sidebarNavigation.goto({ path: 'edit' });
    }
  };

  @computed
  get resetCopy() {
    const sections = unrefPresentSections(
      this.self.sections,
      this.selection.project,
    );

    let hasText = false;
    for (const section of sections) {
      const root = section.element.current;
      if (root) {
        for (const el of elementTreeDfs(root)) {
          hasText ||= el.textOverrides.size > 0;
          if (hasText) break;
        }
      }
      if (hasText) break;
    }

    return hasText
      ? () => {
          this.session.analytics.track({
            event: 'project:copy_reset',
            copy_reset_scope: 'page',
          });
          for (const section of sections) {
            const root = section.element.current;
            if (root) {
              for (const el of elementTreeDfs(root)) {
                el.textOverrides.clear();
              }
            }
          }
        }
      : undefined;
  }

  readonly convertToPath = () => {
    const { self } = this;
    self.pageType = 'path';
  };
  readonly convertToPage = () => {
    const { self } = this;
    self.pageType = 'page';
  };
}

function maybeRenameToOldName(name: string) {
  name = name.toLowerCase().trim();
  return name === 'home' || name === '' ? 'Old Home' : name;
}

export const dropPageAt =
  (selection: SelectionStore) =>
  (source: PageSource, destination: PagePosition) => {
    destination.parent.subPages.splice(destination.index, 0, source.page);
    const page = destination.parent.subPages[destination.index];
    const space = findParentSpace(selection.project, page) || 'home';
    // it must already be visible enough if we're dropping it
    selection.set({ space, page }, { disableEnsureVisible: true });
  };

export const onAfterDropPage =
  (project: Project) =>
  (source: PageSource, _destination: PagePosition, mode: DropMode) => {
    if (mode === 'duplicate') return;
    if (!source.from) return;
    const { parent, mutable } = source.from;
    parent.subPages.delete(mutable);
    if (spaceDomain.isMutable(parent) && parent.subPages.length === 0) {
      project.spaces.delete(parent);
    }
  };

function countPagesInclusive(from: Page | Space) {
  let count = 0;
  if (pageDomain.isMutable(from)) count++;
  for (const subPage of from.subPages) count += countPagesInclusive(subPage);
  return count;
}

export function insertPage(
  parent: Page | Space,
  index: number,
  selection: SelectionStore,
  state?: PageState,
) {
  const space = spaceDomain.isMutable(parent)
    ? parent
    : findParentSpace(selection.project, parent);
  const numExisting = countPagesInclusive(space || selection.project.homePage);
  const newPageState = aPage({ name: `Page ${numExisting + 1}`, ...state });
  parent.subPages.splice(index, 0, newPageState);
  selection.set({ space: space || 'home', page: parent.subPages[index] });
}

export function clearHomepage(project: Project) {
  const { homePage } = project;
  homePage.name = EMPTY_PROJECT.homePage.name;
  homePage.description = '';
  homePage.sections.clear();
  homePage.subPages.clear();
  homePage.commentThreads.clear();
}
